import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

import data from './goldmedalistsinfo.json';
import goldData from './golds.json';
import './dataviz.scss';
import Tooltip from './Tooltip';
import ImageScroll from './ImagesScroll';


const RetirementYears = () => {
  const svgRef = useRef();
  const containerRef = useRef();

  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    drawChart();

    const handleResize = () => {
        drawChart();
    };

    window.addEventListener('resize', handleResize);

    return () => {
        window.removeEventListener('resize', handleResize);
        d3.select(svgRef.current).selectAll('*').remove();
    };
}, []);
  
  const drawChart = () => {
    const container = d3.select(containerRef.current);
    const svg = d3.select(svgRef.current);
    const containerWidth = container.node().clientWidth;
    const containerHeight = container.node().clientHeight;

    

    const margin = { top: 20, right: 0, bottom: 40, left: 35 };
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    svg
      .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`)
      .attr('preserveAspectRatio', 'xMinYMid')
      .attr('width', '100%')
      .attr('height', '100%');

    const svgGroup = svg
      .selectAll('g')
      .data([null])
      .join('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

      svgGroup.selectAll('*').remove();

    const filterActive = data.filter(d => d.retirementYear !== '现役' && d.sw !== 'Winter');


    const retirementCount = d3.rollups(
        filterActive,
      v => v.length,
      d => d.retirementYear
    ).map(([year, count]) => ({ year, count }));

    const years = d3.range(1984, 2024);

    years.forEach(year => {
        const found = retirementCount.find(item => item.year === year);
        if (!found) {
          retirementCount.push({ year, count: 0 });
        }
      });

    retirementCount.sort((a, b) => a.year - b.year);

    const goldsSummer = goldData.filter(d => d.sw !== 'Winter');

    const goldCounts = d3.rollups(
        goldsSummer,
        v => ({
          count: v.length,
          sw: v[0].sw // Assuming all entries for the same year have the same sw value
        }),
        d => d.year
      ).map(([year, { count, sw }]) => ({ year, count, sw }));

      goldCounts.sort((a, b) => a.year - b.year);

    // const margin = { top: 20, right: 50, bottom: 40, left: 40 };
    // const width = 960 - margin.left - margin.right;
    // const height = 500 - margin.top - margin.bottom;

    // const svg = d3.select(svgRef.current)
    //   .attr('class', '.chart-container')
    //   .attr('width', width + margin.left + margin.right)
    //   .attr('height', height + margin.top + margin.bottom)
    //   .append('g')
    //   .attr("viewBox", `0 0 500 960`)
    //   .attr('transform', `translate(${margin.left},${margin.top})`);

    const x = d3.scaleBand()
      .domain(years)
      .range([0, width])
      .padding(0.1);

    const y1 = d3.scaleLinear()
      .domain([0, d3.max(retirementCount, d => d.count)])
      .nice()
      .range([height, 0]);

    const y2 = d3.scaleLinear()
      .domain([0, d3.max(goldCounts, d => d.count)])
      .nice()
      .range([height, 0]);

    svgGroup.append('g')
      .attr('class', 'grid-lines')
      .selectAll('line')
      .data(x.domain().filter(year => year % 4 === 0))
      .enter().append('line')
      .attr('x1', d => x(d) + x.bandwidth() / 2)
      .attr('x2', d => x(d) + x.bandwidth() / 2)
      .attr('y1', 0)
      .attr('y2', height);

    const xAxis = g => g
      .attr('transform', `translate(0,${height})`)
      .attr('class', 'x-axis')
      .call(d3.axisBottom(x)
        .tickSize(0)
        .tickValues(x.domain().filter(year => year % 4 === 0))
            .tickSizeOuter(0))
      .selectAll('.tick text')
      .attr('dy', '1.5em');

    const yAxisLeft = g => g
      .call(d3.axisLeft(y1).tickSize(0))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick text').attr('transform', 'translate(-10,0)'))
      .attr('class','y-axis');

    const yAxisRight = g => g
      .attr('transform', `translate(${width},0)`)
      .call(d3.axisRight(y2).tickSize(0))
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick text').attr('transform', 'translate(10,0)'))
      .attr('class','y-axis');

    
    svgGroup.append('g').call(yAxisLeft);
    svgGroup.append('g').call(yAxisRight);

    svgGroup.selectAll('.bar')
      .data(retirementCount)
      .enter().append('rect')
      .attr('class', 'bar')
      .attr('x', d => x(d.year))
      .attr('y', d => y1(d.count))
      .attr('width', x.bandwidth())
      .attr('height', d => height - y1(d.count))
      .attr('fill', '#9BBBA5')
      .on('mouseover', (event, d) => {
        setTooltipContent(`
          <div class="tooltip__content">
          <span class="tooltip__details"><b>${d.year}</b></span></br>
          <span class="tooltip__title">${d.count} ${ d.count > 1 ? `athletes` : `athlete`} total retired</span></div>
          `)
        setTooltipPosition({ x: event.pageX, y: event.pageY })
        setHovered(event.target);
      })
      .on('mouseout', () => {
        setTooltipContent(null);
        setHovered(null);
      })
      ;

      const line = d3.line()
      .x(d => x(d.year) + x.bandwidth() / 2)
      .y(d => y2(d.count));

    svgGroup.append('path')
      .datum(goldCounts)
      .attr('fill', 'none')
      .attr('stroke', '#ECA500')
      .attr('opacity', '0.08')
      .attr('stroke-width', 5)
      .attr('d', line);



    svgGroup.selectAll('.dot')
      .data(goldCounts)
      .enter().append('circle')
      .attr('class', 'dot')
      .attr('cx', d => x(d.year) + x.bandwidth() / 2)
      .attr('cy', d => y2(d.count))
      .attr('r', 6)
      .attr('fill', '#FFDC8A')
      .attr('stroke', '#ECA600')
      .attr('stroke-width', 1)
      .on('mouseover', (event, d) => {
        setTooltipContent(`
          <div class="tooltip__content">
          <span class="tooltip__details"><b>${d.year}</b></span></br>
          <span class="tooltip__title">${d.count} gold medals won</span></div>
          `)
        setTooltipPosition({ x: event.pageX, y: event.pageY })
        setHovered(event.target);
      })
      .on('mouseout', () => {
        setTooltipContent(null);
        setHovered(null);
      })
      ;

      svgGroup.append('g').call(xAxis);
      
    // // Y-axis label for retirement count
    //   svgGroup.append('text')
    //   .attr('text-anchor', 'end')
    //   .attr('transform', 'rotate(-90)')
    //   .attr('x', -height / 2)
    //   .attr('y', -margin.left + 20)
    //   .text('Number of Retirements');

    //   // Y-axis label for gold count
    //   svgGroup.append('text')
    //   .attr('text-anchor', 'end')
    //   .attr('transform', 'rotate(-90)')
    //   .attr('x', -height / 2)
    //   .attr('y', width + margin.right - 10)
    //   .text('Number of Gold Medals');


      // return () => {
      //   svgGroup.selectAll('.dot').remove(); 
      // };
  }


  return (
    <div>
      
      <div className='content'>
        <div className='content__text'>
        <h2>IV. Retirement peaks right after the Summer Olympic Games</h2>
        </div>
      </div>
      <div ref={containerRef} className='chart-container'>

      <div style={{ display:'flex', justifyContent:'space-between' }}>
                <div className='legend__item' >
                  <div className='legend__color' style={{ backgroundColor:'#9BBBA5', marginLeft:'6px' }}></div>
                  <div className='legend__label'>Total number of Olympic <br/>champions retiring</div>
                  </div>
      
                <div className='legend__item' id='gold-legend'>
                  
                    <div className='legend__label' style={{ marginRight:'6px', textAlign:'right' }}>
                      Total number of gold medals won by<br/> China in the Summer Olympics</div><div className='legend__color round' style={{ backgroundColor:'#FFDC8A', border:'1px solid #ECA600' }}></div>
                  </div>
              </div>

        <svg ref={svgRef} id='retirement-years'/>
        <Tooltip content={tooltipContent} position={tooltipPosition} hovered={hovered} />
      </div>

      <div className='content'>
      <div className='scroll__caption chart-caption'>Note: Hover to view specific information on the number of Olympic champions retiring and the total number of gold medals won that year.</div>

        <div className='content__text'>
          <p>WHYNOT found that in the year of the Summer Olympics and the first year after, the number of Olympic champions retiring showed significant peaks. The concentrated years of retirement are related to the Summer Olympic cycle. This trend is not as evident in the Winter Olympics, likely because China has far fewer Winter Olympic champions than Summer Olympic champions.</p>

          <p>Since 1949, Mao Zedong repeatedly <a href='https://www.dswxyjy.org.cn/n/2015/1026/c222139-27741712-5.html' target='_blank'>emphasized</a> that the Chinese nation must shed the "Sick Man of East Asia" label and personally led efforts to establish the "All-China Sports Federation" to develop the country's sports industry. Under the state-sponsored sports system, the philosophy of "gold medals above all" and "utilitarianism" became deeply rooted in people’s hearts. In 1995, China's State Sports Commission introduced the “<a href='https://www.dswxyjy.org.cn/n/2015/1026/c222139-27741712-5.html' target='_blank'>Plan for Olympic Glory（奥运争光计划）</a>”, which concentrated resources on niche <a href='https://www.wainao.me/wainao-reads/Chinese-womens-football-team-out-of-the-world-cup-08222023' target='_blank'>sports</a> with a high gold medal potential. This strategy, often compared to the ancient Chinese "Tian Ji Horse Racing" <a href='https://m.thepaper.cn/newsDetail_forward_13785565' target='_blank'>strategy</a>, allowed China to rapidly transform from a nation with zero Olympic gold medals to a dominant force in global sports.</p>

          <p>As a result, athletes—whether by choice or obligation—came to view the Olympics as both an opportunity to serve the country and the pinnacle of their careers. However, failure and withdrawal from competition were long considered unacceptable by the public. The experience of Li Ning, China's first "Prince of Gymnastics," is a prime example. After faltering in the 1988 Seoul Olympics, he faced immense backlash, with some even symbolically <a href='https://www.163.com/dy/article/GH6NT0OS0525CH6S.html' target='_blank'>offering him a knife</a>, implying he should take responsibility for his failure. Similarly, in the 2008 Beijing Olympics, hurdler Liu Xiang withdrew due to injury, falling from a national hero to being mocked as "<a href='https://www.163.com/dy/article/GEQH4JML05521MK6.html' target='_blank'>Liu paopao (Liu the Runner)</a>" and subjected to online abuse. Chinese Olympians not only bear the pressure of competition but also the heavy responsibility of shaping China’s image as a sports powerhouse. Li Ning once reflected in an interview that, at that time, people needed gold medals, not sports.</p>

          <p>But nearly 40 years later, has China's "gold medal above all" mindset truly changed?</p>

        </div>

        <ImageScroll
        src1='/images/AP386704502152.jpg'
        caption1="August 7, 2012, Chinese athlete Liu Xiang falls down during the men's 110-meter hurdles preliminary round at the London Summer Olympics athletics competition, resting against a hurdle. (AP / Anja Niedringhaus)"
        src2='/images/AP724311142354.jpg'
        caption2='March 13, 2015, Olympic gold medalist and CPPCC member Liu Xiang arriving at the Great Hall of the People in Beijing to attend the closing ceremony of the CPPCC. (AP Photo / Ng Han Guan)'
         />

         <div className='content__text'>

         <p>In China, the title of Olympic champion can transform a person's life. Most champions secure stable jobs within the system and experience upward social mobility in their household registrations. The Olympic Games carry enormous symbolic weight for these athletes, and their retirement decisions often align with the Olympic cycle.</p>
        <p>However, behind each champion are thousands of ordinary athletes striving for gold medals. Reports <a href='http://sports.people.com.cn/GB/31928/357261/' target='_blank'>indicate</a> that around 4,000 athletes retire in China each year, with 80% facing unemployment, injuries, poverty, and other survival challenges. Without the honor and halo of a championship title, these "non-Olympic champions" often fade into obscurity. Some resort to street performances or manual labor, such as working in bathhouses as massage attendants, and many <a href='https://www.sohu.com/a/525943596_209208' target='_blank'>struggle</a> to find reemployment.</p>

        <p>It is crucial to remember that these athletes, like Olympic champions, endured years of rigorous, closed-door training under the state-sponsored system, sacrificed time with their families, and bore the weight of national expectations. Many also suffer from long-term health issues due to their careers. They, too, deserve the same level of respect and societal support.</p>

          <img src={process.env.PUBLIC_URL + '/images/wave.svg'} alt="" style={{ margin:'0 auto', width:'80%', display:'block', paddingTop:'120px' }} />
         </div>
      </div>
    </div>
  );
}

export default RetirementYears;
